import React, { useState, useEffect, useRef } from 'react'
import './Api.css'
import ContactModal from '../ContactModal/ContactModal'

const CodeBlock = ({ lang, status, html }) => {
  const [copied, setCopied] = useState(false)
  const preRef = useRef(null)
  const copy = () => {
    const text = preRef.current?.innerText || ''
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }
  return (
    <div className="cb">
      <div className="cb-hdr">
        <span className="cb-lang">{lang}</span>
        {status && <span className="cb-ok">{status}</span>}
        <button className="cb-copy" onClick={copy}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>
      <pre ref={preRef} dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  )
}

const TabGroup = ({ tabs }) => {
  const [active, setActive] = useState(0)
  return (
    <>
      <div className="ep-tabs">
        {tabs.map((t, i) => (
          <button key={i} className={`ep-tab${active === i ? ' active' : ''}`} onClick={() => setActive(i)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="ep-tc active">{tabs[active].content}</div>
    </>
  )
}

const EpCard = ({ method, path, name, children }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className={`ep-card${open ? ' open' : ''}`}>
      <div className="ep-hdr" onClick={() => setOpen(o => !o)}>
        <span className={`badge badge-${method.toLowerCase()}`}>{method}</span>
        <span className="ep-path">{path}</span>
        <span className="ep-name">{name}</span>
        <svg className="ep-chevron" width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      {open && <div className="ep-body">{children}</div>}
    </div>
  )
}

const EpCardStatic = ({ method, path, name }) => (
  <div className="ep-card">
    <div className="ep-hdr" style={{ cursor: 'default' }}>
      <span className={`badge badge-${method.toLowerCase()}`}>{method}</span>
      <span className="ep-path">{path}</span>
      <span className="ep-name">{name}</span>
    </div>
  </div>
)


const ParamTable = ({ headers, rows }) => (
  <div className="pt-wrap">
    <table className="pt">
      <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => {
          const hasFourCols = r.length > 3
          return (
            <tr key={i}>
              <td className="pn">{r[0]}</td>
              <td className="pt-type">{r[1]}</td>
              {hasFourCols && <td className={r[2] === 'Required' ? 'pt-req' : 'pt-opt'}>{r[2]}</td>}
              <td className="pt-desc" dangerouslySetInnerHTML={{ __html: r[r.length - 1] }} />
            </tr>
          )
        })}
      </tbody>
    </table>
  </div>
)

const Api = () => {
  const [activeSection, setActiveSection] = useState('auth')
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    const sections = document.querySelectorAll('.api-section[id]')
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id) }),
      { rootMargin: '-15% 0px -75% 0px' }
    )
    sections.forEach(s => io.observe(s))
    return () => io.disconnect()
  }, [])

  const sl = (id, label, icon) => (
    <a className={`sb-link${activeSection === id ? ' active' : ''}`} href={`#${id}`} key={id}>
      {icon}{label}
    </a>
  )

  // syntax highlight helpers — produce raw HTML strings for dangerouslySetInnerHTML
  const k = s => `<span class="ck">"${s}"</span>`
  const v = s => `<span class="sv">"${s}"</span>`
  const n = s => `<span class="nv">${s}</span>`
  const c = s => `<span class="cm">${s}</span>`

  return (
    <>
    <div className="api-page">

      {/* ── Hero ── */}
      <div className="api-hero">
        <div className="api-badge"><span></span> Sendcheap Business API</div>
        <h1>Build with the <em>Sendcheap API</em></h1>
        <p>Integrate collection accounts, bank transfers, bill payments, airtime, crypto payments, and identity verification — all from one unified API.</p>
        <div className="api-hero__actions">
          <button className="btn-v" onClick={() => setModalOpen(true)}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h12a1 1 0 011 1v7a1 1 0 01-1 1H2a1 1 0 01-1-1V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" fill="none"/>
              <path d="M1 5l7 5 7-5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            Get API Access
          </button>
          <a href="#auth" className="btn-o">Browse Docs ↓</a>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="api-stats">
        {[['REST','Architecture'],['Bearer','Auth Method'],['JSON','Data Format'],['99.9%','Uptime SLA']].map(([val, lbl]) => (
          <div key={lbl} className="api-stat">
            <div className="api-stat__val">{val}</div>
            <div className="api-stat__lbl">{lbl}</div>
          </div>
        ))}
      </div>

      {/* ── Layout ── */}
      <div className="api-layout">

        {/* Sidebar */}
        <nav className="api-sidebar">
          <div className="sb-group">
            <div className="sb-label">Getting Started</div>
            {sl('auth', 'Authentication',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="4" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M4.5 4V3a2.5 2.5 0 015 0v1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            )}
            {sl('base-url', 'Base URL & Errors',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M1 7h12M7 1c-1.5 2-2 4-2 6s.5 4 2 6M7 1c1.5 2 2 4 2 6s-.5 4-2 6" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
            )}
          </div>
          <div className="sb-group">
            <div className="sb-label">Payment</div>
            {sl('collection-account', 'Collection Account',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <path d="M2 4h10M2 7h7M2 10h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            )}
            {sl('transfer', 'Transfer',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <path d="M2 5h10M9 2l3 3-3 3M12 9H2M5 12l-3-3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <div className="sb-group">
            <div className="sb-label">VAS</div>
            {sl('bill-payment', 'Bill Payment',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="1" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M4 5h6M4 7h4M4 9h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            )}
            {sl('airtime-data', 'Airtime & Data',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <rect x="3" y="1" width="8" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
                <circle cx="7" cy="10" r="1" fill="currentColor"/>
              </svg>
            )}
            {sl('betting', 'Betting',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <path d="M2 12L5 6l2 3 2-5 3 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {sl('cable-tv', 'Cable TV',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <rect x="1" y="2" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M4 12h6M7 10v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            )}
            {sl('crypto', 'Crypto Payment',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M5 5.5c0-.83.67-1.5 1.5-1.5h1c.83 0 1.5.67 1.5 1.5S8.33 7 7.5 7H6.5C5.67 7 5 7.67 5 8.5S5.67 10 6.5 10h1c.83 0 1.5-.67 1.5-1.5M7 4v6" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
              </svg>
            )}
            {sl('kyc', 'KYC / BVN',
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M2 12c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            )}
          </div>
        </nav>

        {/* Content */}
        <div className="api-content">

          {/* ── Authentication ── */}
          <section className="api-section" id="auth">
            <div className="api-section__hdr">
              <div className="api-icon">
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <rect x="1" y="5" width="16" height="12" rx="2" stroke="#8a73fb" strokeWidth="1.4"/>
                  <path d="M5.5 5V4a3.5 3.5 0 017 0v1" stroke="#8a73fb" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </div>
              <h2>Authentication</h2>
            </div>
            <p className="api-section__desc">
              Every request requires two credentials: a Bearer token in the <code>Authorization</code> header and an App-Token in a separate header.
            </p>
            <div className="auth-card">
              <h3>Getting Your Credentials</h3>
              <p>Contact us via Telegram to receive your <code>business_auth_token</code> and <code>app_token</code>. Separate sandbox and production credentials are issued for each business account.</p>
              <button className="btn-v" style={{ fontSize: '.82rem', padding: '.55rem 1.2rem', cursor: 'pointer' }} onClick={() => setModalOpen(true)}>
                Request API Access
              </button>
            </div>
            <CodeBlock
              lang="Required Headers"
              html={`Authorization: Bearer <span class="sv">{{business_auth_token}}</span>\nApp-Token: <span class="sv">{{app_token}}</span>\nContent-Type: <span class="sv">application/json</span>`}
            />
          </section>

          {/* ── Base URL & Errors ── */}
          <section className="api-section" id="base-url">
            <div className="api-section__hdr">
              <div className="api-icon">
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <circle cx="9" cy="9" r="8" stroke="#8a73fb" strokeWidth="1.4"/>
                  <path d="M1 9h16M9 1c-2 2.5-2.5 5-2.5 8s.5 5.5 2.5 8M9 1c2 2.5 2.5 5 2.5 8s-.5 5.5-2.5 8" stroke="#8a73fb" strokeWidth="1.4"/>
                </svg>
              </div>
              <h2>Base URL &amp; Errors</h2>
            </div>
            <div className="url-bar">
              <span className="lbl">Base URL</span>
              <code>https://api.sendcheap.ng</code>
            </div>
            <p className="api-section__desc" style={{ marginTop: '.85rem' }}>
              All endpoints are prefixed with <code>/business/payment/</code>. Responses follow a consistent JSON envelope.
            </p>
            <CodeBlock
              lang="Success"
              html={`{ ${k('status')}: ${v('success')}, ${k('message')}: ${v('OK')}, ${k('data')}: { ${c('/* payload */')} } }`}
            />
            <CodeBlock
              lang="Error"
              html={`{ ${k('status')}: ${v('error')}, ${k('message')}: ${v('Insufficient balance')}, ${k('code')}: ${n('402')} }`}
            />
            <div className="pt-wrap">
              <table className="pt" style={{ marginTop: '1rem' }}>
                <thead><tr><th>HTTP</th><th>Meaning</th></tr></thead>
                <tbody>
                  {[
                    ['200', '#4ade80', 'Success'],
                    ['400', '#f4a261', 'Bad request — check parameters'],
                    ['401', '#f87171', 'Unauthorized — invalid or missing token'],
                    ['402', '#f87171', 'Payment required — insufficient balance'],
                    ['422', '#f87171', 'Validation error'],
                    ['500', '#f87171', 'Internal server error'],
                  ].map(([code, color, meaning]) => (
                    <tr key={code}>
                      <td style={{ color, fontSize: '.78rem', fontWeight: 600 }}>{code}</td>
                      <td className="pt-desc">{meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <hr className="divider" />

          {/* ── Collection Account ── */}
          <section className="api-section" id="collection-account">
            <div className="api-section__hdr">
              <div className="api-icon">
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path d="M2 5h14M2 9h10M2 13h7" stroke="#8a73fb" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </div>
              <h2>Collection Account</h2>
            </div>
            <p className="api-section__desc">Create virtual bank accounts to receive customer payments, monitor incoming transactions, and manage your wallet balance.</p>

            <EpCard method="POST" path="/business/payment/generate_business_account" name="Generate Business Account">
              <p className="ep-desc">Creates a static virtual bank account tied to your business. Use this as your primary collection account.</p>
              <ParamTable
                headers={['Parameter', 'Type', 'Required', 'Description']}
                rows={[['account_name', 'string', 'Required', 'Name to display on the virtual account']]}
              />
              <TabGroup tabs={[
                {
                  label: 'Request',
                  content: <CodeBlock lang="JSON Body" html={`{\n  ${k('account_name')}: ${v('David')}\n}`} />
                },
                {
                  label: 'Response',
                  content: <CodeBlock lang="JSON" status="200 OK" html={`{\n  ${k('status')}: ${v('success')},\n  ${k('data')}: {\n    ${k('account_number')}: ${v('0123456789')},\n    ${k('account_name')}: ${v('SENDCHEAP / DAVID')},\n    ${k('bank_name')}: ${v('Wema Bank')},\n    ${k('bank_code')}: ${v('035')}\n  }\n}`} />
                },
              ]} />
            </EpCard>

            <EpCard method="POST" path="/business/payment/generate_dynamic_account" name="Generate Dynamic Account">
              <p className="ep-desc">Generates a one-time virtual account for a specific invoice. The account auto-expires once payment is received.</p>
              <ParamTable
                headers={['Parameter', 'Type', 'Required', 'Description']}
                rows={[
                  ['invoice_id',   'string',  'Required', 'Your unique invoice/order reference'],
                  ['account_name', 'string',  'Required', 'Label to show on the virtual account'],
                  ['tranx_type',   'integer', 'Required', 'Transaction type identifier (e.g. <code style="color:#a997fc">1</code>)'],
                  ['tranx_amount', 'string',  'Required', 'Expected payment amount (NGN)'],
                ]}
              />
              <TabGroup tabs={[
                {
                  label: 'Request',
                  content: <CodeBlock lang="JSON Body" html={`{\n  ${k('invoice_id')}: ${v('TESTIhNG1d3')},\n  ${k('account_name')}: ${v('FEE PAYMENT')},\n  ${k('tranx_type')}: ${n('1')},\n  ${k('tranx_amount')}: ${v('200')}\n}`} />
                },
                {
                  label: 'Response',
                  content: <CodeBlock lang="JSON" status="200 OK" html={`{\n  ${k('status')}: ${v('success')},\n  ${k('data')}: {\n    ${k('account_number')}: ${v('0123456789')},\n    ${k('account_name')}: ${v('FEE PAYMENT')},\n    ${k('bank_name')}: ${v('Wema Bank')},\n    ${k('invoice_id')}: ${v('TESTIhNG1d3')},\n    ${k('amount')}: ${v('200')},\n    ${k('expires_at')}: ${v('2026-05-06T23:59:59Z')}\n  }\n}`} />
                },
              ]} />
            </EpCard>

            <EpCard method="POST" path="/business/payment/update_account_number_name" name="Update Account Name">
              <ParamTable
                headers={['Parameter', 'Type', 'Required', 'Description']}
                rows={[['account_name', 'string', 'Required', 'New name to set on the account']]}
              />
              <CodeBlock lang="JSON Body" html={`{ ${k('account_name')}: ${v('David')} }`} />
            </EpCard>

            <EpCard method="GET" path="/business/payment/get_wallet_balance" name="Get Wallet Balance">
              <p className="ep-desc">Returns the current NGN wallet balance for your business account. No request body required.</p>
              <CodeBlock lang="Response" status="200 OK" html={`{\n  ${k('status')}: ${v('success')},\n  ${k('data')}: {\n    ${k('balance')}: ${v('50000.00')},\n    ${k('currency')}: ${v('NGN')}\n  }\n}`} />
            </EpCard>

            <EpCard method="GET" path="/business/payment/verify_transaction" name="Verify Transaction">
              <ParamTable
                headers={['Query Param', 'Type', 'Required', 'Description']}
                rows={[['invoice_id', 'string', 'Required', 'The invoice ID from generate_dynamic_account']]}
              />
              <CodeBlock lang="Example Request" html={`GET /business/payment/verify_transaction?invoice_id=<span class="sv">TESTIhNG1d3</span>`} />
              <CodeBlock lang="Response" status="200 OK" html={`{\n  ${k('status')}: ${v('success')},\n  ${k('data')}: {\n    ${k('invoice_id')}: ${v('TESTIhNG1d3')},\n    ${k('payment_status')}: ${v('paid')},\n    ${k('amount')}: ${v('200')},\n    ${k('paid_at')}: ${v('2026-05-06T14:22:10Z')}\n  }\n}`} />
            </EpCard>

            <EpCard method="GET" path="/business/payment/verify_transaction_by_session_id" name="Verify by Session ID">
              <ParamTable
                headers={['Query Param', 'Type', 'Required', 'Description']}
                rows={[['session_id', 'string', 'Required', 'NIP session ID of the inbound transfer']]}
              />
              <CodeBlock lang="Example Request" html={`GET /business/payment/verify_transaction_by_session_id?session_id=<span class="sv">000015231121015916043489005573</span>`} />
            </EpCard>

            <EpCard method="GET" path="/business/payment/get_money_received_transaction" name="List Received Payments">
              <ParamTable
                headers={['Query Param', 'Type', 'Description']}
                rows={[
                  ['cursor',     'integer', 'Pagination cursor, start at <code style="color:#a997fc">0</code>'],
                  ['search_key', 'string',  'Optional search term (invoice ID, account number, etc.)'],
                ]}
              />
              <CodeBlock lang="Example Request" html={`GET /business/payment/get_money_received_transaction?cursor=<span class="nv">0</span>&search_key=`} />
            </EpCard>

            <EpCard method="GET" path="/business/payment/repush_notification" name="Repush Settlement">
              <p className="ep-desc">Re-triggers the settlement webhook for a specific invoice. Use this if your webhook missed a payment notification.</p>
              <ParamTable
                headers={['Query Param', 'Type', 'Required', 'Description']}
                rows={[['invoice_id', 'string', 'Required', 'Invoice ID to repush notification for']]}
              />
              <CodeBlock lang="Example Request" html={`GET /business/payment/repush_notification?invoice_id=<span class="sv">sendcheap-093d0caffb2e0e202423b914492657</span>`} />
            </EpCard>
          </section>

          <hr className="divider" />

          {/* ── Transfer ── */}
          <section className="api-section" id="transfer">
            <div className="api-section__hdr">
              <div className="api-icon">
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path d="M2 6h14M11 2l5 4-5 4M16 12H2M7 16l-5-4 5-4" stroke="#8a73fb" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2>Transfer</h2>
            </div>
            <p className="api-section__desc">Resolve bank accounts, send funds to any Nigerian bank, and verify the status of outgoing transfers.</p>

            <EpCard method="GET" path="/business/payment/get_bank_list" name="Get Bank List">
              <p className="ep-desc">Returns a list of all supported Nigerian banks with their codes. Use <code style={{ color: '#a997fc' }}>bank_code</code> for resolve and transfer calls.</p>
              <CodeBlock lang="Response" status="200 OK" html={`{\n  ${k('status')}: ${v('success')},\n  ${k('data')}: [\n    { ${k('bank_name')}: ${v('Zenith Bank')}, ${k('bank_code')}: ${v('000015')} },\n    { ${k('bank_name')}: ${v('Access Bank')}, ${k('bank_code')}: ${v('044')} },\n    { ${k('bank_name')}: ${v('GTBank')},      ${k('bank_code')}: ${v('058')} }\n  ]\n}`} />
            </EpCard>

            <EpCard method="POST" path="/business/payment/resolve_transfer" name="Resolve Account Number">
              <p className="ep-desc">Look up the account name for a given account number and bank code before initiating a transfer.</p>
              <ParamTable
                headers={['Parameter', 'Type', 'Required', 'Description']}
                rows={[
                  ['account_number', 'string', 'Required', '10-digit NUBAN account number'],
                  ['bank_code',      'string', 'Required', 'Bank code from get_bank_list'],
                ]}
              />
              <TabGroup tabs={[
                {
                  label: 'Request',
                  content: <CodeBlock lang="JSON Body" html={`{\n  ${k('account_number')}: ${v('2208818698')},\n  ${k('bank_code')}: ${v('000015')}\n}`} />
                },
                {
                  label: 'Response',
                  content: <CodeBlock lang="JSON" status="200 OK" html={`{\n  ${k('status')}: ${v('success')},\n  ${k('data')}: {\n    ${k('account_name')}: ${v('CONFIDENCE WANOGHO')},\n    ${k('account_number')}: ${v('2208818698')},\n    ${k('bank_name')}: ${v('Zenith Bank')}\n  }\n}`} />
                },
              ]} />
            </EpCard>

            <EpCard method="POST" path="/business/payment/transfer_funds" name="Make Transfer">
              <p className="ep-desc">Send funds from your wallet to any Nigerian bank account. Always call <code style={{ color: '#a997fc' }}>resolve_transfer</code> first to confirm the account name.</p>
              <ParamTable
                headers={['Parameter', 'Type', 'Required', 'Description']}
                rows={[
                  ['account_name',   'string', 'Required', 'Recipient account name (from resolve)'],
                  ['account_number', 'string', 'Required', 'Recipient 10-digit NUBAN'],
                  ['bank_name',      'string', 'Required', 'Recipient bank name'],
                  ['bank_code',      'string', 'Required', 'Recipient bank code'],
                  ['tranx_id',       'string', 'Required', 'Your unique transaction reference ID'],
                  ['bank_id',        'string', 'Required', 'Internal bank session identifier'],
                  ['client_bvn',     'string', 'Required', 'Recipient BVN for compliance'],
                  ['client_id',      'string', 'Required', 'Internal client ID'],
                  ['tranx_remark',   'string', 'Required', 'Transfer narration / remark'],
                  ['amount',         'string', 'Required', 'Amount in NGN'],
                ]}
              />
              <TabGroup tabs={[
                {
                  label: 'Request',
                  content: <CodeBlock lang="JSON Body" html={`{\n  ${k('account_name')}: ${v('CONFIDENCE WANOGHO')},\n  ${k('account_number')}: ${v('2208818698')},\n  ${k('bank_name')}: ${v('Zenith Bank')},\n  ${k('bank_code')}: ${v('000015')},\n  ${k('tranx_id')}: ${v('4905763814660884')},\n  ${k('bank_id')}: ${v('090110231120224905763814660884')},\n  ${k('client_bvn')}: ${v('22000000091')},\n  ${k('client_id')}: ${v('138715')},\n  ${k('tranx_remark')}: ${v('Payment for services')},\n  ${k('amount')}: ${v('100')}\n}`} />
                },
                {
                  label: 'Response',
                  content: <CodeBlock lang="JSON" status="200 OK" html={`{\n  ${k('status')}: ${v('success')},\n  ${k('data')}: {\n    ${k('tranx_id')}: ${v('4905763814660884')},\n    ${k('session_id')}: ${v('000015231121015916043489005573')},\n    ${k('status')}: ${v('processing')}\n  }\n}`} />
                },
              ]} />
            </EpCard>

            <EpCard method="GET" path="/business/payment/verify_transfer" name="Verify Transfer">
              <ParamTable
                headers={['Query Param', 'Type', 'Required', 'Description']}
                rows={[['tranx_id', 'string', 'Required', 'Your transaction ID from transfer_funds']]}
              />
              <CodeBlock lang="Example Request" html={`GET /business/payment/verify_transfer?tranx_id=<span class="sv">4905763814660884</span>`} />
              <CodeBlock lang="Response" status="200 OK" html={`{\n  ${k('status')}: ${v('success')},\n  ${k('data')}: {\n    ${k('tranx_id')}: ${v('4905763814660884')},\n    ${k('transfer_status')}: ${v('successful')},\n    ${k('amount')}: ${v('100')},\n    ${k('completed_at')}: ${v('2026-05-06T15:10:00Z')}\n  }\n}`} />
            </EpCard>
          </section>

          <hr className="divider" />

          {[
            {
              id: 'bill-payment',
              title: 'Bill Payment',
              icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><rect x="1" y="1" width="16" height="16" rx="2" stroke="#8a73fb" strokeWidth="1.4"/><path d="M5 7h8M5 10h5M5 13h4" stroke="#8a73fb" strokeWidth="1.4" strokeLinecap="round"/></svg>,
              desc: 'Pay electricity, water, and other utility bills programmatically. Supports all major Nigerian DISCOs and water boards with instant token delivery.',
              endpoints: [['GET','/business/bills/billers','Get Billers'],['POST','/business/bills/validate','Validate Meter'],['POST','/business/bills/pay','Pay Bill']],
            },
            {
              id: 'airtime-data',
              title: 'Airtime & Data',
              icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><rect x="4" y="1" width="10" height="16" rx="2" stroke="#8a73fb" strokeWidth="1.4"/><circle cx="9" cy="13.5" r="1.2" fill="#8a73fb"/></svg>,
              desc: 'Purchase airtime and data bundles instantly for MTN, Airtel, Glo, and 9mobile.',
              endpoints: [['POST','/business/mobile/airtime','Buy Airtime'],['GET','/business/mobile/data/plans','Get Data Plans'],['POST','/business/mobile/data','Buy Data Bundle']],
            },
            {
              id: 'betting',
              title: 'Betting',
              icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M2 15L5.5 8l3 4 2.5-7 4 10" stroke="#8a73fb" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
              desc: 'Fund customer accounts on Bet9ja, Sportybet, 1xBet, Betway, and more.',
              endpoints: [['POST','/business/betting/fund','Fund Betting Account'],['GET','/business/betting/operators','Get Operators']],
            },
            {
              id: 'cable-tv',
              title: 'Cable TV',
              icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><rect x="1" y="2" width="16" height="11" rx="2" stroke="#8a73fb" strokeWidth="1.4"/><path d="M5 15h8M9 13v2" stroke="#8a73fb" strokeWidth="1.4" strokeLinecap="round"/></svg>,
              desc: 'Renew DSTV, GOtv, and Startimes subscriptions for any smartcard number.',
              endpoints: [['POST','/business/cable/subscribe','Subscribe / Renew'],['GET','/business/cable/providers','Get Providers']],
            },
            {
              id: 'crypto',
              title: 'Crypto Payment',
              icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="8" stroke="#8a73fb" strokeWidth="1.4"/><path d="M6.5 6.5c0-1.1.9-2 2-2h1c1.1 0 2 .9 2 2s-.9 2-2 2H7.5c-1.1 0-2 .9-2 2s.9 2 2 2h1c1.1 0 2-.9 2-2M9 4.5v9" stroke="#8a73fb" strokeWidth="1.3" strokeLinecap="round"/></svg>,
              desc: 'Generate crypto payment addresses, accept USDT, BTC, ETH and more, with real-time webhook notifications on payment receipt.',
              endpoints: [['POST','/business/crypto/payment-address','Generate Address'],['POST','/business/crypto/send','Send Crypto'],['GET','/business/crypto/rates','Get Rates']],
            },
            {
              id: 'kyc',
              title: 'KYC / BVN Verification',
              icon: <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="5.5" r="3.5" stroke="#8a73fb" strokeWidth="1.4"/><path d="M2 16c0-3.87 3.13-7 7-7s7 3.13 7 7" stroke="#8a73fb" strokeWidth="1.4" strokeLinecap="round"/></svg>,
              desc: 'Verify customer BVN, NIN, and bank account identity data. Fully CBN and NDPR compliant.',
              endpoints: [['POST','/business/kyc/bvn/verify','Verify BVN'],['POST','/business/kyc/nin/verify','Verify NIN'],['POST','/business/kyc/account/verify','Resolve Bank Account']],
            },
          ].map(({ id, title, icon, desc, endpoints }, idx) => (
            <React.Fragment key={id}>
              {idx > 0 && <hr className="divider" />}
              <section className="api-section" id={id}>
                <div className="api-section__hdr">
                  <div className="api-icon">{icon}</div>
                  <h2>{title}</h2>
                </div>
                <p className="api-section__desc">{desc}</p>
                {endpoints.map(([method, path, name]) => (
                  <EpCard key={path} method={method} path={path} name={name} />
                ))}
              </section>
            </React.Fragment>
          ))}

        </div>
      </div>
    </div>

    <ContactModal
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      title="Request API Access"
      subject="API Access Request — Sendcheap"
    />
    </>
  )
}

export default Api
